// src/app/[subdomain]/layout.tsx
"use client";

import { useState, useEffect, useCallback, useMemo, Suspense } from "react";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { Phone, MapPin, Mail } from "lucide-react";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import AuthModal from "@/components/store/AuthModal";
import CartDrawer from "@/components/store/CartDrawer";
import AffiliateTracker from "@/components/store/AffiliateTracker";

import NavbarSectionRenderer from "@/components/store/sections/navbar";
import { resolveCurrentSubdomain } from "@/components/store/sections/navbar/brandingConfig";
import CustomerChatWidget from "@/components/chat/CustomerChatWidget";
import SoftNavbar from "@/components/templates/SoftClassifiedStorefront/SoftNavbar";
import SoftFooter from "@/components/templates/SoftClassifiedStorefront/SoftFooter";

// 🌟 React 19 / Next.js 16 Rapid Navigation & Google Translate DOM Crash Guard
if (typeof window !== "undefined" && typeof Node !== "undefined") {
  const win = window as any;
  if (!win.__XYLO_DOM_PATCHED__) {
    win.__XYLO_DOM_PATCHED__ = true;

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(child: T): T {
      if (child && child.parentNode !== this) {
        if (child.parentNode) {
          try {
            return child.parentNode.removeChild(child);
          } catch {
            return child;
          }
        }
        return child;
      }
      try {
        return originalRemoveChild.apply(this, arguments as any) as T;
      } catch {
        return child;
      }
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(
      newNode: T,
      referenceNode: Node | null
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        try {
          return this.appendChild(newNode);
        } catch {
          return newNode;
        }
      }
      try {
        return originalInsertBefore.apply(this, arguments as any) as T;
      } catch {
        return this.appendChild(newNode);
      }
    };
  }
}

export function getMediaUrl(rawUrl?: any, subdomain = "nextlook") {
  if (!rawUrl) return "/logo.png";

  let cleanPath = typeof rawUrl === "string" ? rawUrl : rawUrl?.url || "";
  if (!cleanPath || typeof cleanPath !== "string") return "/logo.png";

  if (cleanPath.startsWith("blob:") || cleanPath.startsWith("data:")) {
    return cleanPath;
  }

  if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
    try {
      const parsed = new URL(cleanPath);
      cleanPath = parsed.pathname;
    } catch {
      cleanPath = cleanPath.replace(/^https?:\/\/[^\/]+/, "");
    }
  }

  if (!cleanPath.startsWith("/")) {
    cleanPath = `/${cleanPath}`;
  }

  if (typeof window !== "undefined") {
    const isLocal =
      window.location.hostname.includes("localhost") ||
      window.location.hostname === "127.0.0.1";

    if (isLocal) {
      return `http://${subdomain}.localhost:8000${cleanPath}`;
    }
    return cleanPath;
  }

  return cleanPath;
}

const getSafeText = (val: any, fallback = ""): string => {
  if (typeof val === "string") return val.trim();
  if (typeof val === "number") return String(val);
  if (val && typeof val === "object") {
    return (val.address || val.street || val.name || "").trim() || fallback;
  }
  return fallback;
};

const formatMerchantBrandName = (sub: string): string => {
  const clean = (sub || "").trim();
  if (!clean) return "Online Store";
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

interface StoreLayoutProps {
  children: React.ReactNode;
}

export default function StoreLayout({ children }: StoreLayoutProps) {
  const routeParams = useParams();
  const rawRouteSub = Array.isArray(routeParams?.subdomain)
    ? routeParams?.subdomain[0]
    : (routeParams?.subdomain as string);
  const subdomain = resolveCurrentSubdomain(rawRouteSub);
  const pathname = usePathname();

  const isAdminRoute = pathname?.includes("/admin");
  const isBranchRoute = pathname?.includes("/b/") || pathname?.endsWith("/b");
  const isAffiliateRoute = pathname?.includes("/affiliate");
  const isIsolatedRoute = isAdminRoute || isBranchRoute || isAffiliateRoute;

  const fallbackBrandName = useMemo(() => formatMerchantBrandName(subdomain), [subdomain]);

  // 🌟 Centralized Multi-Tenant Google OAuth Token Bridge
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash && window.location.hash.includes("access_token=")) {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const accessToken = params.get("access_token");
      if (accessToken && window.opener) {
        try {
          window.opener.postMessage(
            { type: "XYLO_SAAS_GOOGLE_ACCESS_TOKEN", token: accessToken },
            "*"
          );
          window.close();
        } catch (e) {
          console.error("OAuth bridge error:", e);
        }
      }
    }
  }, []);

  const apiBase = useMemo(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `http://${subdomain}.localhost:8000`;
      }
      return "";
    }
    return `http://${subdomain}.localhost:8000`;
  }, [subdomain]);

  const [settings, setSettings] = useState<any>({
    theme: "default",
    primary_color: "#059669",
    secondary_color: "#047857",
    font_family: "Poppins",
    show_announcement_bar: true,
    announcement_text: "Sign up and GET 20% OFF on your first order!",
    announcement_phone: "+880 1700-000000",
    store_name: fallbackBrandName,
    store_tagline: "Authentic Online Shopping",
    store_description: "We deliver authentic organic produce, fashion, and household essentials nationwide.",
    contact_address: "Dhaka, Bangladesh",
    contact_phone: "+880 1700-000000",
    contact_email: "",
    logo: null,
    navbar_branding: null,
    section_designs: {
      navbar: "modern_pill",
    },
  });

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(
        `${apiBase}/api/v1/stores/settings/?subdomain=${encodeURIComponent(subdomain)}`,
        {
          headers: {
            Accept: "application/json",
            "X-Tenant-Subdomain": subdomain,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        const logoVal = data.logo || data.store_logo;
        const resolvedStoreName = getSafeText(data.store_name, fallbackBrandName);

        setSettings({
          theme: getSafeText(data.theme, "default"),
          primary_color: getSafeText(data.primary_color, "#059669"),
          secondary_color: getSafeText(data.secondary_color, "#047857"),
          font_family: getSafeText(data.font_family, "Poppins"),
          show_announcement_bar: data.show_announcement_bar !== false,
          announcement_text: getSafeText(data.announcement_text, "Sign up and GET 20% OFF on your first order!"),
          announcement_phone: getSafeText(data.announcement_phone, "+880 1700-000000"),
          store_name: resolvedStoreName === "." ? fallbackBrandName : resolvedStoreName,
          store_tagline: getSafeText(data.store_tagline, ""),
          store_description: getSafeText(
            data.store_description,
            "We deliver authentic organic produce, fashion, and household essentials nationwide."
          ),
          contact_address: getSafeText(data.contact_address, "Dhaka, Bangladesh"),
          contact_phone: getSafeText(data.contact_phone || data.announcement_phone, "+880 1700-000000"),
          contact_email: getSafeText(data.contact_email, ""),
          logo: typeof logoVal === "string" ? logoVal : logoVal?.url || null,
          navbar_branding: data.navbar_branding || data.theme_config?.navbar_branding || null,
          section_designs: data.section_designs || data.theme_config?.section_designs || { navbar: "modern_pill" },
          app_distribution: data.app_distribution || null,
        });
      }
    } catch (e) {
      console.error("Theme load error:", e);
    }
  }, [apiBase, subdomain, fallbackBrandName]);

  useEffect(() => {
    loadSettings();

    const handleSettingsUpdate = (e: any) => {
      if (e.detail) {
        setSettings((prev: any) => ({ ...prev, ...e.detail }));
      } else {
        loadSettings();
      }
    };

    window.addEventListener("storeSettingsUpdated", handleSettingsUpdate);
    window.addEventListener("navbarBrandingUpdated", handleSettingsUpdate);
    return () => {
      window.removeEventListener("storeSettingsUpdated", handleSettingsUpdate);
      window.removeEventListener("navbarBrandingUpdated", handleSettingsUpdate);
    };
  }, [loadSettings]);

  const primaryColor = getSafeText(settings.primary_color, "#059669");
  const secondaryColor = getSafeText(settings.secondary_color, "#047857");
  const fontFamily = getSafeText(settings.font_family, "Poppins");
  const currentStoreName = getSafeText(settings.store_name, fallbackBrandName);
  const cleanMerchantTitle =
    !currentStoreName || currentStoreName === "." || currentStoreName.toLowerCase().includes("xylo")
      ? fallbackBrandName
      : currentStoreName;

  const navbarLogoUrl = settings.logo ? getMediaUrl(settings.logo, subdomain) : null;
  const activeNavbarVariant = settings?.section_designs?.navbar || "modern_pill";

  useEffect(() => {
    if (typeof document === "undefined") return;

    const tagline = getSafeText(settings.store_tagline, "");
    const desiredTitle = isAdminRoute
      ? `${cleanMerchantTitle} | Merchant Admin`
      : isBranchRoute
      ? `${cleanMerchantTitle} | Branch Portal`
      : isAffiliateRoute
      ? `${cleanMerchantTitle} | Affiliate Partner Portal`
      : tagline
      ? `${cleanMerchantTitle} — ${tagline}`
      : cleanMerchantTitle;

    if (!document.title.startsWith("🔔")) {
      document.title = desiredTitle;
    }

    if (navbarLogoUrl) {
      const selectors = ["link[rel~='icon']", "link[rel='shortcut icon']", "link[rel='apple-touch-icon']"];
      selectors.forEach((sel) => {
        let link = document.querySelector(sel) as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement("link");
          link.rel = sel.includes("apple") ? "apple-touch-icon" : "icon";
          document.head.appendChild(link);
        }
        if (link.href !== navbarLogoUrl) {
          link.href = navbarLogoUrl;
        }
      });
    }
  }, [cleanMerchantTitle, settings.store_tagline, isAdminRoute, isBranchRoute, isAffiliateRoute, navbarLogoUrl, pathname]);

  const dynamicThemeStyles = `
    :root {
      --store-primary: ${primaryColor};
      --store-secondary: ${secondaryColor};
      --store-font: '${fontFamily}', sans-serif;
    }
    body {
      font-family: var(--store-font) !important;
      top: 0px !important;
      position: static !important;
    }

    .goog-te-banner-frame,
    .goog-te-balloon-frame,
    .goog-te-gadget,
    .goog-te-gadget-icon,
    .goog-te-gadget-simple,
    .goog-te-menu-value,
    #goog-gt-tt,
    .goog-tooltip,
    .goog-tooltip:hover,
    .skiptranslate > iframe,
    iframe.skiptranslate,
    iframe.goog-te-banner-frame,
    body > .skiptranslate,
    [class*="VIpgJd-ZVi9od"],
    [class*="VIpgJd-yAWNEb"],
    [id*="goog-gt-"],
    .VIpgJd-ZVi9od-aZ2wEe-wOHMyf,
    .VIpgJd-ZVi9od-aZ2wEe-OiiCO,
    .VIpgJd-ZVi9od-ORHb-OEVmcd,
    .VIpgJd-ZVi9od-l4eHX-hSRGPd {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
      width: 0px !important;
      height: 0px !important;
      position: absolute !important;
      left: -9999px !important;
      top: -9999px !important;
      z-index: -9999 !important;
    }

    .goog-text-highlight {
      background-color: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }

    #xylo_store_translate_container {
      position: fixed !important;
      bottom: -9999px !important;
      left: -9999px !important;
      width: 1px !important;
      height: 1px !important;
      opacity: 0 !important;
      pointer-events: none !important;
      overflow: hidden !important;
      z-index: -9999 !important;
    }

    main h2.text-white,
    main h1.text-white,
    main h3.text-white,
    main h2[class*="text-white"],
    main h3[class*="text-white"] {
      color: #0f172a !important;
      -webkit-text-fill-color: #0f172a !important;
    }

    .bg-\\[\\#059669\\],
    .bg-\\[\\#047857\\],
    .bg-emerald-600,
    .bg-blue-600,
    .bg-blue-500 {
      background-color: ${primaryColor} !important;
    }

    .bg-emerald-50 {
      background-color: #ecfdf5 !important;
    }

    .bg-emerald-500\\/10,
    .bg-emerald-500\\/20,
    .bg-emerald-950\\/40,
    .bg-emerald-950\\/80 {
      background-color: ${primaryColor}18 !important;
    }

    .hover\\:bg-\\[\\#047857\\]:hover,
    .hover\\:bg-emerald-700:hover,
    .hover\\:bg-emerald-600:hover,
    .hover\\:bg-blue-500:hover,
    .hover\\:bg-blue-700:hover {
      background-color: ${secondaryColor} !important;
    }

    .text-\\[\\#059669\\],
    .text-emerald-600,
    .text-emerald-500,
    .text-blue-600,
    .hover\\:text-blue-600:hover {
      color: ${primaryColor} !important;
    }

    .border-\\[\\#059669\\],
    .border-emerald-600,
    .border-emerald-500,
    .border-blue-600,
    .border-l-emerald-500 {
      border-color: ${primaryColor} !important;
    }
  `;

  if (isIsolatedRoute) {
    return (
      <AuthProvider>
        <CartProvider subdomain={subdomain}>
          <div className="min-h-screen w-full">{children}</div>
        </CartProvider>
      </AuthProvider>
    );
  }

  const isCustomTheme = settings.theme === "classified_soft" || settings.theme === "neon_studio";

  return (
    <AuthProvider>
      <CartProvider subdomain={subdomain}>
        <div className="min-h-screen flex flex-col w-full relative">
          <Suspense fallback={null}>
            <AffiliateTracker subdomain={subdomain} />
          </Suspense>
          <style dangerouslySetInnerHTML={{ __html: dynamicThemeStyles }} />

          {isCustomTheme ? (
            <div className="min-h-screen bg-[#F0F4F9] text-slate-800 flex flex-col justify-between font-sans relative">
              <SoftNavbar subdomain={subdomain} storeName={cleanMerchantTitle} appDistribution={settings.app_distribution} />
              <main className="flex-1 w-full">{children}</main>
              <SoftFooter
                subdomain={subdomain}
                storeName={cleanMerchantTitle}
                phone={getSafeText(settings.contact_phone || settings.announcement_phone, "+880 1700-000000")}
              />
              <AuthModal subdomain={subdomain} />
              <CartDrawer subdomain={subdomain} />
              <CustomerChatWidget subdomain={subdomain} />
            </div>
          ) : (
            <div className="min-h-screen flex flex-col bg-[#F9FAFB] text-slate-800 transition-colors relative">
              <NavbarSectionRenderer
                variant={activeNavbarVariant}
                subdomain={subdomain}
                storeName={cleanMerchantTitle}
                storeTagline={settings.store_tagline}
                logo={navbarLogoUrl}
                branding={settings.navbar_branding}
                primaryColor={primaryColor}
                showAnnouncementBar={settings.show_announcement_bar}
                announcementText={getSafeText(settings.announcement_text, "Sign up and GET 20% OFF on your first order!")}
                announcementPhone={getSafeText(settings.announcement_phone, "+880 1700-000000")}
                appDistribution={settings.app_distribution}
              />

              <main className="flex-1">{children}</main>

              <footer
                className="text-white pt-16 pb-8 border-t mt-12"
                style={{ backgroundColor: secondaryColor, borderColor: `${primaryColor}40` }}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-4 gap-10">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      {navbarLogoUrl && settings.navbar_branding?.brandDisplayMode !== "name_only" && (
                        <div className="bg-white p-1.5 rounded-xl shadow-sm inline-flex items-center justify-center shrink-0">
                          <img
                            src={navbarLogoUrl}
                            alt={cleanMerchantTitle}
                            className="h-8 max-h-8 w-auto object-contain rounded-md"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        </div>
                      )}
                      {currentStoreName !== "." && (
                        <span className="font-extrabold text-2xl tracking-tight capitalize text-white">
                          {cleanMerchantTitle}
                        </span>
                      )}
                    </div>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {getSafeText(settings.store_description, "We deliver authentic organic produce, fashion, and household essentials.")}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-white text-base mb-4">Company</h4>
                    <ul className="space-y-2 text-sm text-white/80">
                      <li>About Us</li>
                      <li>Careers</li>
                      <li>Blog & News</li>
                      <li>
                        <Link href="/affiliate" className="text-amber-300 hover:underline font-bold">
                          Affiliate Partner Program (অ্যাফিলিয়েট)
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-bold text-white text-base mb-4">Customer Care</h4>
                    <ul className="space-y-2 text-sm text-white/80">
                      <li>Track Your Order</li>
                      <li>Help & FAQs</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-bold text-white text-base mb-4">Contact Info</h4>
                    <p className="text-sm text-white/80 flex items-center gap-2">
                      <MapPin className="w-4 h-4 shrink-0 text-amber-400" />
                      <span>{getSafeText(settings.contact_address, "Dhaka, Bangladesh")}</span>
                    </p>
                    <p className="text-sm font-semibold flex items-center gap-2" style={{ color: "#FBBF24" }}>
                      <Phone className="w-4 h-4 shrink-0" />
                      <span>{getSafeText(settings.contact_phone || settings.announcement_phone, "+880 1700-000000")}</span>
                    </p>
                    {settings.contact_email ? (
                      <p className="text-xs text-white/80 flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 shrink-0 text-teal-300" />
                        <span>{getSafeText(settings.contact_email)}</span>
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-12 pt-6 border-t border-white/10 text-xs text-white/60 flex justify-between">
                  <p>© {new Date().getFullYear()} {cleanMerchantTitle}. All rights reserved.</p>
                  <p>Privacy Policy • Terms of Service</p>
                </div>
              </footer>

              <AuthModal subdomain={subdomain} />
              <CartDrawer subdomain={subdomain} />
              <CustomerChatWidget subdomain={subdomain} />
            </div>
          )}
        </div>
      </CartProvider>
    </AuthProvider>
  );
}